import isNotAuth from '@/router/middleware/auth/isNotAuth'
import auth from '@/router/middleware/auth/auth'
import checkWorkspace from '@/router/middleware/checkWorkspace'
import trialEnded from '@/router/middleware/auth/trialEnded'
import isNotTrial from '@/router/middleware/isNotTrial'
import isOwner from '@/router/middleware/isOwner'
import isWorkspaceOwner from '@/router/middleware/isWorkspaceOwner'
import isNotReviewsTrialEnded from '@/router/middleware/auth/isNotReviewsTrialEnded'
import isNotEmptyWorkspace from '@/router/middleware/isNotEmptyWorkspace'
import isAccountDisabled from '@/router/middleware/auth/isAccountDisabled'
import isAccountNotDisabled from '@/router/middleware/auth/isAccountNotDisabled'
import notFound from '@/router/middleware/notFound'
import checkPermissions from '@/router/middleware/checkPermissions'
import isQuestionnaireProgressComplete from '@/router/middleware/auth/isQuestionnaireProgressComplete'
import isNotCompleteQuestionnaire from '@/router/middleware/auth/isNotCompleteQuestionnaire'

import { ROUTES } from '@/constants/routes'
import { PERMISSION_NAMES } from '@/constants/workspace/permissionSettings'

export const BASE_ROUTES = [
    {
        path: '/auth',
        name: ROUTES.auth,
        redirect: { name: 'AuthLogin' },
        component: () => import('@/views/auth/Auth.vue'),
        children: [{
            path: 'login',
            name: ROUTES.authLogin,
            meta: {
                layout: 'empty',
                middleware: isNotAuth,
                title: 'Dialics Login',
                reference: ROUTES.auth,
            },
            component: () => import('@/views/auth/login/AuthLogin.vue'),
        }, {
            path: 'reset-password',
            name: ROUTES.authReset,
            meta: { layout: 'empty', middleware: isNotAuth, reference: ROUTES.auth },
            component: () => import('@/views/auth/reset-password/ResetPassword.vue'),
        }, {
            path: 'reset-password/:token',
            name: ROUTES.authSetNewPassword,
            meta: {
                layout: 'empty',
                reference: ROUTES.auth,
            },
            component: () => import('@/views/set-new-password/SetNewPassword.vue'),
        }],
    }, {
        path: '/signup',
        name: ROUTES.register,
        meta: {
            layout: 'empty', middleware: isNotAuth, title: 'Dialics Signup', reference: ROUTES.register,
        },
        component: () => import('@/views/register/Register.vue'),
    }, {
        path: '/invite/:slug',
        name: ROUTES.inviteUser,
        meta: {
            layout: 'empty',
            reference: ROUTES.inviteUser,
        },
        component: () => import('@/views/invite-users/InviteUsers.vue'),
    }, {
        path: '/:workspaceSlug/campaigns/:campaignSlug/offers/:offerSlug/applications/:requestSlug/connect',
        name: ROUTES.inviteOfferUser,
        meta: {
            layout: 'empty',
            reference: ROUTES.inviteOfferUser,
        },
        component: () => import('@/views/invite-offer-users/InviteOfferUsers.vue'),
    }, {
        path: '/:workspace/reporting',
        name: ROUTES.reporting,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.reporting,
        },
        component: () => import('@/views/call-tracking/reporting/Reporting.vue'),
    }, {
        path: '/:workspace/campaigns',
        name: ROUTES.manageCampaigns,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageCampaigns,
            permission: PERMISSION_NAMES.view_campaigns,
        },
        component: () => import('@/views/call-tracking/campaigns/ManageCampaigns.vue'),
    }, {
        path: '/:workspace/campaigns/:slug',
        name: ROUTES.detailCampaigns,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageCampaigns,
            permission: PERMISSION_NAMES.view_campaigns,
        },
        component: () => import('@/views/call-tracking/campaigns/DetailCampaign.vue'),
    }, {
        path: '/:workspace/global-offers',
        name: ROUTES.manageGlobalOffers,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageGlobalOffers,
            permission: PERMISSION_NAMES.view_global_offers,
        },
        component: () => import('@/views/call-tracking/global-offers/ManageGlobalOffers.vue'),
    }, {
        path: '/:workspace/global-offers/:slug',
        name: ROUTES.detailGlobalOffer,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageGlobalOffers,
            permission: PERMISSION_NAMES.view_global_offers,
        },
        component: () => import('@/views/call-tracking/global-offers/DetailGlobalOffer.vue'),
    }, {
        path: '/preview/:offerWorkspace/campaigns/:slug/offers/:offerSlug',
        name: ROUTES.offerPreview,
        meta: {
            layout: 'empty',
            reference: ROUTES.offerPreview,
        },
        component: () => import('@/views/offer-preview/OfferPreview.vue'),
    }, {
        path: '/:workspace/rtb-log',
        name: ROUTES.rtbLog,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, isNotTrial, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.rtbLog,
            permission: PERMISSION_NAMES.view_rtb_bid_log,
        },
        component: () => import('@/views/call-tracking/rtb-log/ManageRtbLog.vue'),
    }, {
        path: '/:workspace/tracking-numbers',
        name: ROUTES.manageNumbers,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageNumbers,
            permission: PERMISSION_NAMES.view_phone_numbers,
        },
        component: () => import('@/views/call-tracking/tracking-numbers/ManageTrackingNumbers.vue'),
    }, {
        path: '/:workspace/tracking-numbers/buy-tracking-numbers',
        name: ROUTES.buyNumbers,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageNumbers,
            permission: PERMISSION_NAMES.view_phone_numbers,
        },
        component: () => import('@/views/call-tracking/tracking-numbers/BuyTrackingNumbers.vue'),
    }, {
        path: '/:workspace/tracking-numbers/:slug',
        name: ROUTES.detailNumber,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageNumbers,
            permission: PERMISSION_NAMES.view_phone_numbers,
        },
        component: () => import('@/views/call-tracking/tracking-numbers/DetailTrackingNumber.vue'),
    }, {
        path: '/:workspace/pools',
        name: ROUTES.managePools,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.managePools,
            permission: PERMISSION_NAMES.view_phone_number_pools,
        },
        component: () => import('@/views/call-tracking/number-pools/ManageNumberPools.vue'),
    }, {
        path: '/:workspace/pools/:slug',
        name: ROUTES.detailPool,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.managePools,
            permission: PERMISSION_NAMES.view_phone_number_pools,
        },
        component: () => import('@/views/call-tracking/number-pools/DetailNumberPool.vue'),
    }, {
        path: '/:workspace/blocked-numbers',
        name: ROUTES.blockNumber,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.blockNumber,
            permission: PERMISSION_NAMES.view_blocked_numbers,
        },
        component: () => import('@/views/call-tracking/blocked-numbers/ManageBlockedNumbers.vue'),
    }, {
        path: '/:workspace/voip-shield',
        name: ROUTES.voipShield,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.voipShield,
            permission: PERMISSION_NAMES.view_voip_black_lists,
        },
        component: () => import('@/views/call-tracking/voip-shield/ManageVoipShield.vue'),
    }, {
        path: '/:workspace/voip-shield/:slug',
        name: ROUTES.detailVoipShield,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.voipShield,
            permission: PERMISSION_NAMES.view_voip_black_lists,
        },
        component: () => import('@/views/call-tracking/voip-shield/DetailVoipShield.vue'),
    }, {
        path: '/:workspace/tcpa-shield',
        name: ROUTES.tcpaShield,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.tcpaShield,
            permission: PERMISSION_NAMES.view_tcpa_shield,
        },
        component: () => import('@/views/call-tracking/tcpa-shield/ManageTcpaShield.vue'),
    }, {
        path: '/:workspace/tcpa-shield/:slug',
        name: ROUTES.detailTcpaShield,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.tcpaShield,
            permission: PERMISSION_NAMES.view_tcpa_shield,
        },
        component: () => import('@/views/call-tracking/tcpa-shield/DetailTcpaShield.vue'),
    }, {
        path: '/:workspace/destinations',
        name: ROUTES.manageDestinations,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageDestinations,
            permission: PERMISSION_NAMES.view_destinations,
        },
        component: () => import('@/views/call-tracking/destinations/ManageDestinations.vue'),
    }, {
        path: '/:workspace/destinations/:slug',
        name: ROUTES.detailDestination,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageDestinations,
            permission: PERMISSION_NAMES.view_destinations,
        },
        component: () => import('@/views/call-tracking/destinations/DetailDestination.vue'),
    }, {
        path: '/:workspace/destination-groups',
        name: ROUTES.manageDestinationGroups,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageDestinationGroups,
            permission: PERMISSION_NAMES.view_destination_groups,
        },
        component: () => import('@/views/call-tracking/destination-groups/ManageDestinationGroups.vue'),
    }, {
        path: '/:workspace/destination-groups/:slug',
        name: ROUTES.detailDestinationGroup,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageDestinationGroups,
            permission: PERMISSION_NAMES.view_destination_groups,
        },
        component: () => import('@/views/call-tracking/destination-groups/DetailDestinationGroup.vue'),
    }, {
        path: '/:workspace/ping-logs',
        name: ROUTES.pingPostLog,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, isNotTrial, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.pingPostLog,
            permission: PERMISSION_NAMES.view_ping_log,
        },
        component: () => import('@/views/call-tracking/ping-log/ManagePingLog.vue'),
    }, {
        path: '/:workspace/buyers',
        name: ROUTES.manageBuyers,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageBuyers,
            permission: PERMISSION_NAMES.view_buyers,
        },
        component: () => import('@/views/call-tracking/buyers/ManageBuyers.vue'),
    }, {
        path: '/:workspace/buyers/:slug',
        name: ROUTES.detailBuyer,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageBuyers,
            permission: PERMISSION_NAMES.view_buyers,
        },
        component: () => import('@/views/call-tracking/buyers/DetailBuyer.vue'),
    }, {
        path: '/:workspace/vendors',
        name: ROUTES.manageVendors,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageVendors,
            permission: PERMISSION_NAMES.view_vendors,
        },
        component: () => import('@/views/call-tracking/vendors/ManageVendors.vue'),
    }, {
        path: '/:workspace/vendors/:slug',
        name: ROUTES.detailVendor,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageVendors,
            permission: PERMISSION_NAMES.view_vendors,
        },
        component: () => import('@/views/call-tracking/vendors/DetailVendor.vue'),
    }, {
        path: '/:workspace/call-flows',
        name: ROUTES.callFlows,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.callFlows,
            permission: PERMISSION_NAMES.view_call_flows,
        },
        component: () => import('@/views/call-tracking/call-flows/CallFlows.vue'),
    }, {
        path: '/:workspace/call-flows/:slug',
        name: ROUTES.detailCallFlows,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.callFlows,
            permission: PERMISSION_NAMES.view_call_flows,
        },
        component: () => import('@/views/call-tracking/call-flows/DetailCallFlows.vue'),
    }, {
        path: '/:workspace/disputes',
        name: ROUTES.disputes,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.disputes,
            permission: PERMISSION_NAMES.view_disputes,
        },
        component: () => import('@/views/call-tracking/dispute/ManageDispute.vue'),
    }, {
        path: '/:workspace/offers',
        name: ROUTES.manageOffers,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageOffers,
        },
        component: () => import('@/views/call-tracking/offers/ManageOffers.vue'),
    }, {
        path: '/:workspace/offers/:slug',
        name: ROUTES.detailOffer,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageOffers,
        },
        component: () => import('@/views/call-tracking/offers/DetailOffer.vue'),
    }, {
        path: '/:workspace/offers-requests',
        name: ROUTES.manageRequests,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageRequests,
        },
        component: () => import('@/views/call-tracking/offer-requests/ManageRequests.vue'),
    }, {
        path: '/:workspace/offers-requests/:slug/offer',
        name: ROUTES.detailOfferFromRequests,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageRequests,
        },
        component: () => import('@/views/call-tracking/offer-requests/DetailOfferFromRequests.vue'),
    }, {
        path: '/:workspace/traffic-sources',
        name: ROUTES.manageTrafficSources,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageTrafficSources,
            permission: PERMISSION_NAMES.view_traffic_sources,
        },
        component: () => import('@/views/call-tracking/traffic-sources/ManageTrafficSources.vue'),
    }, {
        path: '/:workspace/traffic-sources/:slug',
        name: ROUTES.detailTrafficSource,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageTrafficSources,
            permission: PERMISSION_NAMES.view_traffic_sources,
        },
        component: () => import('@/views/call-tracking/traffic-sources/DetailTrafficSource.vue'),
    }, {
        path: '/:workspace/integrations',
        name: ROUTES.manageIntegrations,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/manage-integrations/ManageIntegrations.vue'),
    }, {
        path: '/:workspace/manage-leads',
        name: ROUTES.manageLeads,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageLeads,
            permission: PERMISSION_NAMES.view_leads,
        },
        component: () => import('@/views/call-tracking/leads/ManageLeads.vue'),
    }, {
        path: '/:workspace/custom-parameters',
        name: ROUTES.customParameters,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.customParameters,
            permission: PERMISSION_NAMES.view_custom_parameters,
        },
        component: () => import('@/views/call-tracking/custom-parameters/CustomParameters.vue'),
    }, {
        path: '/:workspace/integration/google-ads',
        name: ROUTES.googleAdsIntegration,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/google-integration/GoogleIntegration.vue'),
    }, {
        path: '/:workspace/integration/facebook-ads',
        name: ROUTES.facebookAdsIntegration,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/facebook-integration/FacebookIntegration.vue'),
    }, {
        path: '/:workspace/integration/facebook-ads/traffic-sources/:slug',
        name: ROUTES.facebookAdsIntegrationTrafficSource,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/traffic-sources/DetailTrafficSource.vue'),
    }, {
        path: '/:workspace/integration/facebook-ads/traffic-sources/:slug/event-logs',
        name: ROUTES.facebookAdsIntegrationTrafficSourceEventLogs,
        meta: {
            layout: 'main',
            middleware: [auth, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/facebook-integration/FacebookEventsLog.vue'),
    }, {
        path: '/:workspace/integration/google-ads/traffic-sources/:slug',
        name: ROUTES.googleAdsIntegrationTrafficSource,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/traffic-sources/DetailTrafficSource.vue'),
    }, {
        path: '/:workspace/integration/incoming-webhooks',
        name: ROUTES.incomingWebhooksIntegration,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/incoming-webhooks-integration/IncomingWebhooksIntegration.vue'),
    }, {
        path: '/:workspace/integration/incoming-webhooks/:slug',
        name: ROUTES.detailIncomingWebhooksIntegration,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/incoming-webhooks-integration/DetailIncomingWebhookIntegration.vue'),
    }, {
        path: '/:workspace/integration/outgoing-webhooks',
        name: ROUTES.outgoingWebhooksIntegration,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/outgoing-webhooks-integration/OutgoingWebhooksIntegration.vue'),
    }, {
        path: '/:workspace/integration/byoc',
        name: ROUTES.byocIntegration,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/byoc-integration/ByocIntegration.vue'),
        redirect: { name: ROUTES.byocNumbers }, // 👈 дефолтный редирект
        children: [
            {
                path: 'carriers',
                name: ROUTES.byocCarriers,
                component: () => import('@/views/call-tracking/integrations/byoc-integration/components/sections/ByocIntegrationCarriersSection.vue'),
                meta: {
                    permission: PERMISSION_NAMES.view_integrations,
                },
            },
            {
                path: 'numbers',
                name: ROUTES.byocNumbers,
                component: () => import('@/views/call-tracking/integrations/byoc-integration/components/sections/ByocIntegrationNumbersSection.vue'),
                meta: {
                    permission: PERMISSION_NAMES.view_integrations,
                },
            },
        ],
    }, {
        path: '/:workspace/integration/byoc/carriers/:slug',
        name: ROUTES.detailByocIntegrationCarrier,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/byoc-integration/DetailByocInegrationCarrier.vue'),
    }, {
        path: '/:workspace/integration/outgoing-webhooks/:slug',
        name: ROUTES.detailOutgoingWebhooksIntegration,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/outgoing-webhooks-integration/DetailOutgoingWebhookIntegration.vue'),
    }, {
        path: '/:workspace/integration/outgoing-webhooks-log/:slug',
        name: ROUTES.detailOutgoingWebhooksLogIntegration,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, checkPermissions, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageIntegrations,
            permission: PERMISSION_NAMES.view_integrations,
        },
        component: () => import('@/views/call-tracking/integrations/outgoing-webhooks-integration/DetailOutgoingWebhookLogIntegration.vue'),
    }, {
        path: '/:account/white-label',
        name: ROUTES.whiteLabel,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, isOwner, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.whiteLabel,
        },
        component: () => import('@/views/settings/white-label/WhiteLabel.vue'),
    }, {
        path: '/:workspace/getting-start',
        name: ROUTES.gettingStart,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.gettingStart,
        },
        component: () => import('@/views/getting-start/GettingStart.vue'),
    }, {
        path: '/:account/workspaces/:workspace',
        name: ROUTES.manageWorkspace,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, isWorkspaceOwner, isOwner, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageWorkspace,
        },
        component: () => import('@/views/workspaces/ManageWorkspaces.vue'),
    }, {
        path: '/:account/workspaces/:workspace/roles/:slug',
        name: ROUTES.detailWorkspaceRole,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, isWorkspaceOwner, isOwner, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageWorkspace,
        },
        component: () => import('@/views/workspaces/DetailWorkspaceRole.vue'),
    }, {
        path: '/account-details',
        name: ROUTES.accountDetails,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.accountDetails,
        },
        component: () => import('@/views/settings/account-details/AccountDetails.vue'),
    }, {
        path: '/notification-settings',
        name: ROUTES.notificationSettings,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.notificationSettings,
        },
        component: () => import('@/views/settings/notification-settings/NotificationSettings.vue'),
    }, {
        path: '/security-settings',
        name: ROUTES.securitySettings,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.securitySettings,
        },
        component: () => import('@/views/settings/security/security-settings/SecuritySettings.vue'),
    }, {
        path: '/:workspace/manage-billing',
        name: ROUTES.manageBilling,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkPermissions, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.manageBilling,
            permission: PERMISSION_NAMES.edit_billing,
        },
        component: () => import('@/views/settings/billing/manage-billing/ManageBilling.vue'),
        redirect: { name: ROUTES.rechargeBalance },
        children: [
            {
                path: 'recharge-balance',
                name: ROUTES.rechargeBalance,
                component: () => import('@/views/settings/billing/recharge-balance/RechargeBalance.vue'),
                meta: {
                    reference: ROUTES.manageBilling,
                    permission: PERMISSION_NAMES.edit_billing,
                },
            },
            {
                path: 'manage-cards',
                name: ROUTES.manageCards,
                component: () => import('@/views/settings/billing/manage-cards/ManageCards.vue'),
                meta: {
                    reference: ROUTES.manageBilling,
                    permission: PERMISSION_NAMES.edit_billing,
                },
            },
            {
                path: 'payment-history',
                name: ROUTES.paymentHistory,
                component: () => import('@/views/settings/billing/payment-history/PaymentHistory.vue'),
                meta: {
                    reference: ROUTES.manageBilling,
                    permission: PERMISSION_NAMES.edit_billing,
                },
            },
        ],
    }, {
        path: '/login-security',
        name: ROUTES.loginSecurity,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.loginSecurity,
        },
        component: () => import('@/views/settings/security/login-security/LoginSecurity.vue'),
    }, {
        path: '/:account/rates-and-expenses',
        name: ROUTES.ratesExpenses,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, isOwner, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.ratesExpenses,
        },
        component: () => import('@/views/settings/billing/rates-and-expenses/RatesAndExpenses.vue'),
    }, {
        path: '/:account/api-access-tokens',
        name: ROUTES.apiAccessTokens,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, isOwner, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.apiAccessTokens,
        },
        component: () => import('@/views/settings/security/api-access-token/ApiAccessTokens.vue'),
    }, {
        path: '/:account/verification',
        name: ROUTES.verification,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, isOwner, isQuestionnaireProgressComplete, isNotReviewsTrialEnded],
            reference: ROUTES.verification,
        },
        component: () => import('@/views/verification/VerificationPage.vue'),
    }, {
        path: '/:workspace/questionnaire',
        name: ROUTES.questionnaire,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, isOwner, isNotCompleteQuestionnaire],
            reference: ROUTES.questionnaire,
        },
        component: () => import('@/views/questionnaire/QuestionnairePage.vue'),
    }, {
        path: '/:workspace/workspace-activity',
        name: ROUTES.workspaceActivity,
        meta: {
            layout: 'main',
            middleware: [auth, isAccountNotDisabled, isOwner, checkWorkspace, isQuestionnaireProgressComplete, trialEnded],
            reference: ROUTES.workspaceActivity,
        },
        component: () => import('@/views/settings/security/workspace-activity/ManageWorkspaceActivity.vue'),
    }, {
        path: '/not-found-workspace',
        name: ROUTES.notFoundWorkspace,
        meta: {
            layout: 'workspace',
            middleware: [auth, isAccountNotDisabled, isQuestionnaireProgressComplete, trialEnded, isNotEmptyWorkspace],
            reference: ROUTES.notFoundWorkspace,
        },
        component: () => import('@/views/NotFoundWorkspace.vue'),
    }, {
        path: '/account-disabled',
        name: ROUTES.accountDisabled,
        meta: {
            layout: 'empty',
            middleware: [auth, isAccountDisabled],
            reference: ROUTES.accountDisabled,
        },
        component: () => import('@/views/account-disabled/AccountDisabled.vue'),
    }, {
        path: '/:pathMatch(.*)*',
        name: ROUTES.notFound,
        meta: {
            layout: 'empty',
            middleware: notFound,
            reference: ROUTES.notFound,
        },
        component: () => import('@/views/NotFound.vue'),
    },
]
